import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { ImagePreview } from '@/components';
import {
  getInvoiceDetail,
  Invoice,
  InvoiceParty,
  InvoiceLocation,
  InvoiceImage,
  InvoiceHistoryEntry,
} from '@/services/api/invoices';
import {
  InvoiceStatusBadge,
  InvoiceAgingPill,
  getInvoiceStatusLabel,
  getInvoiceExceptionLabel,
} from './InvoiceStatusBadge';
import { InvoiceAction } from './InvoiceActionModal';

// Only an awaiting-confirmation invoice can be resolved or disputed.
const ACTIONABLE_STATUS = 'WAITING_CONFIRMATION';

const fmtDate = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY') : '—';

const fmtDateTime = (v?: string | null) =>
  v ? dayjs(v).format('DD MMM YYYY, HH:mm') : '—';

const dash = (v?: string | number | null) =>
  v === null || v === undefined || v === '' ? '—' : v;

// outlet / hub / actor come through as either a {name,…} object or a string.
const partyName = (
  v?: InvoiceParty | InvoiceLocation | string | null
): string => {
  if (!v) return '—';
  return typeof v === 'string' ? v : v.name || '—';
};

// The address only exists on object-shaped locations (e.g. outlet).
const locationAddress = (
  v?: InvoiceLocation | string | null
): string | undefined =>
  v && typeof v !== 'string' ? v.address || undefined : undefined;

// Humanize a document type, e.g. TANDA_TERIMA_PESANAN → "Tanda Terima Pesanan".
const docTypeLabel = (t?: string) =>
  t
    ? t
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Dokumen';

// A clickable document thumbnail that opens a full-screen preview on click.
const DocumentThumb: React.FC<{ image: InvoiceImage }> = ({ image }) => {
  const label = docTypeLabel(image.documentType);
  return (
    <div className="w-16 shrink-0" title={label}>
      <ImagePreview
        src={image.imageUrl}
        alt={label}
        className="h-16 w-16 rounded-lg border border-stroke-soft-200 object-cover"
      />
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border-b border-stroke-soft-200 px-6 py-5 last:border-b-0">
    <h3 className="mb-3.5 text-[11px] font-bold uppercase tracking-[0.08em] text-text-soft-400">
      {title}
    </h3>
    {children}
  </div>
);

const KV: React.FC<{ k: string; children: React.ReactNode }> = ({
  k,
  children,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
    <span className="text-text-sub-600">{k}</span>
    <span className="text-right font-semibold text-text-strong-950">
      {children}
    </span>
  </div>
);

// A single status-change in the Activity timeline (mirrors the prototype's
// .tl-event: a dot + connector on the left, title/time/caption on the right).
const TimelineEvent: React.FC<{
  entry: InvoiceHistoryEntry;
  isCurrent: boolean;
  showConnector: boolean;
}> = ({ entry, isCurrent, showConnector }) => {
  const actorName = entry.changedBy?.name?.trim();
  const actorRole = entry.changedBy?.role?.trim();
  const images = entry.invoiceImages?.images ?? [];
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center self-stretch">
        <span
          className={`mt-1 h-3 w-3 shrink-0 rounded-full border-2 ${
            isCurrent
              ? 'border-primary bg-primary'
              : 'border-success-base bg-success-base'
          }`}
        />
        {showConnector && (
          <span className="my-1 w-0.5 flex-1 bg-stroke-soft-200" />
        )}
      </div>
      <div className={`min-w-0 flex-1 ${showConnector ? 'pb-5' : ''}`}>
        <div className="text-[13px] font-semibold text-text-strong-950">
          {getInvoiceStatusLabel(entry.toStatus)}
        </div>
        <div className="mt-0.5 text-xs text-text-soft-400">
          {fmtDateTime(entry.changedAt)}
        </div>
        {(actorName || actorRole) && (
          <div className="mt-0.5 text-xs text-text-sub-600">
            {actorName || 'System'}
            {actorRole ? ` · ${actorRole}` : ''}
          </div>
        )}
        {entry.note && (
          <div className="mt-1 text-[13px] leading-snug text-text-sub-600">
            {entry.note}
          </div>
        )}
        {images.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {images.map((img, i) => (
              <DocumentThumb key={img.imageId || i} image={img} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface InvoiceDetailDrawerProps {
  // The clicked list row — paints the drawer instantly while the full detail
  // (with history) is fetched. May be null when closed.
  invoice: Invoice | null;
  onClose: () => void;
  // Request a resolve/dispute action — the modal is owned by the page so the
  // same flow serves both this footer and the table-row kebab menu.
  onAction?: (action: InvoiceAction) => void;
}

const InvoiceDetailDrawer: React.FC<InvoiceDetailDrawerProps> = ({
  invoice,
  onClose,
  onAction,
}) => {
  const [enriched, setEnriched] = useState<Invoice | null>(null);
  const [history, setHistory] = useState<InvoiceHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeIdRef = useRef<string | null>(null);

  const load = useCallback(async (id: string) => {
    activeIdRef.current = id;
    setLoading(true);
    setError(null);
    setEnriched(null);
    setHistory([]);
    try {
      const data = await getInvoiceDetail(id);
      if (activeIdRef.current !== id) return; // a newer request superseded this
      setEnriched(data.invoice);
      setHistory(data.history);
    } catch (err) {
      if (activeIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Terjadi kesalahan.');
    } finally {
      if (activeIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (invoice?.id) load(invoice.id);
  }, [invoice?.id, load]);

  if (!invoice) return null;

  // Only the verified detail payload is trusted for the body — never the list
  // row. While loading we show a spinner; on failure an error state. The header
  // keeps the row's identifiers (Invoice # / Airwaybill) for context only.
  const inv = enriched;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 z-50 flex h-full w-[540px] max-w-[95vw] flex-col bg-bg-white-0 shadow-card-lg">
        {/* Header — identifiers come from the clicked row; the verified status
            and aging pills only paint once the detail call succeeds. */}
        <div className="flex shrink-0 items-start justify-between border-b border-stroke-soft-200 px-6 py-5">
          <div className="min-w-0">
            {inv && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <InvoiceStatusBadge status={inv.status} />
                <InvoiceAgingPill days={inv.daysSince} tier={inv.agingTier} />
              </div>
            )}
            <h2 className="truncate font-mono text-xl font-semibold tracking-tight text-text-strong-950">
              {invoice.invoiceNumber}
            </h2>
            {invoice.airwaybill && (
              <p className="mt-0.5 font-mono text-xs text-text-soft-400">
                Airwaybill ·{' '}
                <span className="font-semibold text-text-sub-600">
                  {invoice.airwaybill}
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-text-soft-400" />
          </div>
        ) : error || !inv ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-error-lighter">
              <i className="ri-error-warning-line text-2xl text-error-base" />
            </div>
            <h3 className="text-base font-semibold text-text-strong-950">
              Gagal memuat detail invoice
            </h3>
            <p className="mt-1 max-w-sm text-sm text-text-sub-600">
              {error || 'Detail invoice tidak tersedia saat ini.'}
            </p>
            <button
              type="button"
              onClick={() => invoice.id && load(invoice.id)}
              className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-stroke-sub-300 bg-bg-white-0 px-4 py-2 text-sm font-medium text-text-sub-600 hover:bg-bg-weak-50"
            >
              <i className="ri-refresh-line" />
              Coba lagi
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Details */}
            <Section title="Details">
              <KV k="Client">{partyName(inv.client)}</KV>
              <KV k="Outlet">{partyName(inv.outlet)}</KV>
              {locationAddress(inv.outlet) && (
                <KV k="Outlet address">
                  <span className="font-normal text-text-sub-600">
                    {locationAddress(inv.outlet)}
                  </span>
                </KV>
              )}
              <KV k="Hub">{partyName(inv.hub)}</KV>
              <KV k="Rider">{partyName(inv.rider)}</KV>
              <KV k="Items">{dash(inv.itemsCount)}</KV>
              <KV k="Issue date">{fmtDate(inv.issueDate)}</KV>
              <KV k="Aging">
                <InvoiceAgingPill days={inv.daysSince} tier={inv.agingTier} />
              </KV>
              {inv.batchId && (
                <KV k="Batch">
                  <span className="font-mono">{inv.batchId}</span>
                </KV>
              )}
            </Section>

            {/* Exception — only when the gateway flags one */}
            {(inv.exceptionCode || inv.returnedAt || inv.notes) && (
              <Section title="Exception & notes">
                {inv.exceptionCode && (
                  <KV k="Exception code">
                    <span className="text-error-base">
                      {getInvoiceExceptionLabel(inv.exceptionCode)}
                    </span>
                  </KV>
                )}
                {inv.returnedAt && (
                  <KV k="Returned at">{fmtDateTime(inv.returnedAt)}</KV>
                )}
                {inv.notes && <KV k="Notes">{inv.notes}</KV>}
              </Section>
            )}

            {/* Activity — status-change history from the detail endpoint */}
            <Section title="Activity">
              {history.length > 0 ? (
                <div>
                  {/* Latest change first — reverse the chronological payload. */}
                  {[...history].reverse().map((h, i, arr) => (
                    <TimelineEvent
                      key={h.id}
                      entry={h}
                      isCurrent={i === 0}
                      showConnector={i < arr.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <KV k="Created">{fmtDateTime(inv.createdAt)}</KV>
              )}
            </Section>
          </div>
        )}

        {/* Action footer — only an awaiting-confirmation invoice can be acted on. */}
        {!loading && !error && inv && inv.status === ACTIONABLE_STATUS && (
          <div className="flex shrink-0 gap-3 border-t border-stroke-soft-200 px-6 py-4">
            <button
              type="button"
              onClick={() => onAction?.('resolve')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-success-base bg-success-base px-4 py-2.5 text-sm font-semibold text-static-white hover:bg-success-dark"
            >
              <i className="ri-checkbox-circle-line text-base" />
              Resolve
            </button>
            <button
              type="button"
              onClick={() => onAction?.('dispute')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-error-base bg-error-base px-4 py-2.5 text-sm font-semibold text-static-white hover:bg-error-dark"
            >
              <i className="ri-error-warning-line text-base" />
              Dispute
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default InvoiceDetailDrawer;
